import Preact, {Component} from "preact";
import socket, { MessageTypes } from "../../socket";
import hark from "hark";
import VideoCallLobbyComponent from "../../components/VideoCallLobby/VideoCallLobby";
import {withNavigation} from "../../contexts/NavigationContext";
import {getRoom} from "../../utils";
import {withGlobals} from "../../contexts/GlobalContext";
import {withAlert} from "../../contexts/AlertContext";
import VideoCallRating from "../VideoCallRating/VideoCallRating";

let speechEvents = null;
let localMediaStream = null;
let peerConnection = null;
let remoteStream = null;

class VideoCallLobby extends Component {
  constructor(props) {
    super(props);
    this.state = {
      streamingSince: null,
      videoStatus: "loading",
      isVideoEnabled: true,
      isAudioEnabled: true,
      isSpeaking: false,
      isSidebarOpen: false,
      unreadMessagesCount: 0,
      arePermissionsEnabled: true,
      message: "",
      isConnectionAvailable: false
    };
    this.localVideo = Preact.createRef();
    this.remoteVideo = Preact.createRef();
  }

  componentDidUpdate(previousProps, previousState, snapshot) {
    const { streamingSince } = this.state;
    const { partnerStreamingSince, roomId } = this.props;
    const hasStreamingSinceChanged = this.state.streamingSince !== previousState.streamingSince;
    const hasPartnerStreamingSinceChanged = this.props.partnerStreamingSince !== previousProps.partnerStreamingSince;

    if ((hasStreamingSinceChanged || hasPartnerStreamingSinceChanged) && partnerStreamingSince && streamingSince) {
      const isInitiator = streamingSince > partnerStreamingSince;
      if (isInitiator) {
        this.doCall();
      }
      this.props.alert.hideAlert();
    }
    // The partner has stopped streaming, we can just create another peerConnection until he comes back
    if (previousProps.partnerStreamingSince && !partnerStreamingSince) {
      this.createPeerConnection();
      if (this.props.globals.options.requestVideoCallRating)
        this.props.alert.showAlertComponent(<VideoCallRating onSubmit={() => {
          this.props.alert.hideAlert();
          this.props.alert.showAlert("Obrigado pela sua resposta!", "Você já pode fechar esta página para sair.", {hideDismiss: true});
        }} />);
      else {
        const opts = {hideDismiss: true};
        if (this.props.globals.options.ratingUrl)
          opts.btn1 = {onClick: () => window.open(this.props.globals.options.ratingUrl, "_blank"), title: "AVALIAR ATENDIMENTO"};
        this.props.alert.showAlert("O seu contato saiu da chamada", "Você já pode fechar esta página para sair também, ou pode aguardá-lo retornar.", opts);
      }
    }

    // Once we learn in which room we are, we must tell the server so that we receive back status of our partner
    if (!previousProps.roomId && this.props.roomId) {
      socket.emit(MessageTypes.UPDATE_STATUS, {
        userData: {
          focusedRoom: roomId
        }
      });
    }

    if (previousProps.messages !== this.props.messages) {
      this.updateAppBarRight();
    }
  }

  updateAppBarRight = () => {
    const { internalId } = this.props.globals.user;
    const unreadMessagesCount = this.props.messages.filter(m => m.readBy < 2 && !(m.content || "").includes("entrou na chamada de vídeo") && m.senderInternalId !== internalId).length;
    this.setState({unreadMessagesCount});
  }

  componentDidMount() {
    this.updateAppBarRight();
    const mediaStreamConstraints = {
      video: true,
      audio: true,
    };

    navigator.mediaDevices.getUserMedia(mediaStreamConstraints)
      .then(this.gotLocalMediaStream).catch(this.handleLocalMediaStreamError);

    socket.on("video_call_signaling", this.handleSignalingMessage);

    this.updateWindowDimensions();
    window.addEventListener("resize", this.updateWindowDimensions);
  }

  onToggleSidebar = () => {
    this.props.updateOrganization().then(() => {
      const { route, navigate } = this.props.navigation;
      navigate({path: "/video-call-lobby", params: {...route.params, isChatOpen: !this.state.isSidebarOpen}});
      const room = getRoom(this.props.rooms, this.props.roomId, this.props.contactInternalId);
      socket.emit(MessageTypes.UPDATE_STATUS, {
        userData: {
          focusedRoom: room && room.id
        }
      });
      this.setState({isSidebarOpen: !this.state.isSidebarOpen});
    });
  }

  componentWillUnmount() {
    socket.off("video_call_signaling", this.handleSignalingMessage);
    socket.emit(MessageTypes.UPDATE_STATUS, {
      userData: {
        streamingSince: null,
        focusedRoom: null
      }
    });
    localMediaStream.getTracks().forEach(function(track) {
      track.stop();
    });
    window.removeEventListener("resize", this.updateWindowDimensions);
  }

  updateWindowDimensions = () => {
    this.setState({ innerHeight: window.innerHeight });
  }

  handleSignalingMessage = (body) => {
    const data = body.data;
    if (data.type === "offer") {
      peerConnection.setRemoteDescription(new RTCSessionDescription(data));
      this.doAnswer();
    } else if (data.type === "answer") {
      peerConnection.setRemoteDescription(new RTCSessionDescription(data));
    } else if (data.type === 'candidate') {
      const candidate = new RTCIceCandidate({
        sdpMLineIndex: data.label,
        candidate: data.candidate
      });
      peerConnection.addIceCandidate(candidate);
    }
  };

  gotLocalMediaStream = (mediaStream) => {
    localMediaStream = mediaStream;
    this.setState({isConnectionAvailable: true});
    this.setState({videoStatus: "available"});
    this.localVideo.current.srcObject = mediaStream;

    if (speechEvents)
      speechEvents.stop();
    speechEvents = hark(mediaStream);

    speechEvents.on("speaking", () => {
      this.setState({isSpeaking: true});
    });
    speechEvents.on("stopped_speaking", () => {
      this.setState({isSpeaking: false});
    });

    // Automatically attempt connection
    this.onStartStream();
  }

  handleLocalMediaStreamError = error => {
    console.log('navigator.getUserMedia error: ', error);
    this.setState({videoStatus: "error"});
    if (error.name === "NotAllowedError") {
      this.setState({ arePermissionsEnabled: false });
    } else {
      this.props.alert.showAlert("Não foi possível acessar sua câmera.", "Por favor, verifique as configurações do seu aparelho, atualize a página e tente novamente.", {hideDismiss: true});
    }
    if (window.Sentry) {
      window.Sentry.captureException(error);
    }
  }

  onToggleVideo = () => {
    const { isVideoEnabled } = this.state;
    localMediaStream.getVideoTracks()[0].enabled = !isVideoEnabled;
    this.setState({isVideoEnabled: !isVideoEnabled});
  };

  onToggleAudio = () => {
    const { isAudioEnabled } = this.state;
    localMediaStream.getAudioTracks()[0].enabled = !isAudioEnabled;
    this.setState({isAudioEnabled: !isAudioEnabled});
  };

  onStartStream = () => {
    const {currentUser, contactInternalId, roomId} = this.props;
    const timestamp = new Date().toISOString();
    socket.emit(MessageTypes.SEND_DIRECT_MESSAGE, {
      message: {
        type: 'text',
        content: currentUser.name + ' entrou na chamada de vídeo',
        sentAt: timestamp,
        senderLabel: currentUser.name,
      },
      receiverInternalId: contactInternalId,
    });
    this.setState({streamingSince: timestamp});
    this.createPeerConnection();
    socket.emit(MessageTypes.UPDATE_STATUS, {
      userData: {
        streamingSince: timestamp,
        focusedRoom: roomId ? roomId: undefined,
      }
    });
  };

  onEndStream = () => {
    socket.emit(MessageTypes.UPDATE_STATUS, {
      userData: {
        streamingSince: null,
        focusedRoom: null
      }
    });
    this.setState({streamingSince: null});
  };

  // Connection
  createPeerConnection = () => {
    try {
      const iceConfiguration = {
        iceServers: [{
          urls: 'stun:stun01.assinasaude.com.br:3478'
        }, {
          urls: 'stun:stun02.assinasaude.com.br:3478'
        }, {
          urls: 'turn:turn01.assinasaude.com.br:3478',
          credential: 'W5Hh4YJvZqi84nKZ',
          username: 'assinasaude'
        }, {
          urls: 'turn:turn02.assinasaude.com.br:3478',
          credential: 'W5Hh4YJvZqi84nKZ',
          username: 'assinasaude'
        }],
        iceTransportPolicy: 'relay'
      };
      peerConnection = new RTCPeerConnection(iceConfiguration);
      peerConnection.onicecandidate = this.handleIceCandidate;
      peerConnection.onaddstream = this.handleRemoteStreamAdded;
      peerConnection.onremovestream = this.handleRemoteStreamRemoved;
      // noinspection JSUnresolvedFunction
      peerConnection.addStream(localMediaStream);
      console.log('Created RTCPeerConnnection');
    } catch (e) {
      console.log('Failed to create PeerConnection, exception: ' + e.message);
      this.props.alert.showAlert("Não foi possível entrar na chamada.", "Por favor, atualize a página e tente novamente.", {hideDismiss: true});
      throw e;
    }
  };

  doCall = () => {
    console.log('Sending offer to peer');
    // noinspection JSCheckFunctionSignatures
    this.setState({message: "Conectando..."});
    peerConnection.createOffer(this.setLocalAndSendMessage, this.handleCreateOfferError);
  };

  setLocalAndSendMessage = (sessionDescription) => {
    const { contactInternalId } = this.props;
    peerConnection.setLocalDescription(sessionDescription);
    socket.emit('video_call_signaling', {contactInternalId, data: sessionDescription});
  }

  handleCreateOfferError = (event) => {
    console.log('createOffer() error: ', event);
  }

  doAnswer = () => {
    console.log('Sending answer to peer.');
    this.setState({message: "Conectando..."});
    peerConnection.createAnswer().then(
      this.setLocalAndSendMessage,
      this.onCreateSessionDescriptionError
    );
  }

  onCreateSessionDescriptionError = (error) => {
    console.log('Failed to create session description: ' + error.toString());
    throw error;
  }

  handleIceCandidate = (event) => {
    const { contactInternalId } = this.props;
    console.log("got icecandidate", event);
    if (event.candidate) {
      socket.emit('video_call_signaling', {
        contactInternalId,
        data: {
          type: 'candidate',
          label: event.candidate.sdpMLineIndex,
          id: event.candidate.sdpMid,
          candidate: event.candidate.candidate
        }
      });
    } else {
      console.log('End of candidates.');
    }
  };

  handleRemoteStreamAdded = (event) => {
    console.log('Remote stream added.');
    this.setState({message: ""});
    remoteStream = event.stream;
    this.remoteVideo.current.srcObject = remoteStream;
  }

  handleRemoteStreamRemoved = (event) => {
    console.log('Remote stream removed. Event: ', event);
    this.setState({message: "A conexão de vídeo foi perdida."});
  }

  render(props, state, context) {
    const { localVideo, remoteVideo, onToggleVideo, onToggleAudio, onStartStream, onEndStream, onToggleSidebar } = this;
    const { isSpeaking, isAudioEnabled, isVideoEnabled, videoStatus, streamingSince, innerHeight, isSidebarOpen, unreadMessagesCount, arePermissionsEnabled, message, isConnectionAvailable } = this.state;
    const { roomId, contactInternalId, partnerStreamingSince } = this.props;

    return <VideoCallLobbyComponent
      videoRef={localVideo}
      remoteVideoRef={remoteVideo}
      isSpeaking={isSpeaking}
      isAudioEnabled={isAudioEnabled}
      isVideoEnabled={isVideoEnabled}
      onToggleVideo={onToggleVideo}
      onToggleAudio={onToggleAudio}
      videoStatus={videoStatus}
      onStartStream={onStartStream}
      onEndStream={onEndStream}
      isStreaming={Boolean(streamingSince)}
      isPartnerStreaming={Boolean(partnerStreamingSince)}
      innerHeight={innerHeight}
      roomId={roomId}
      contactInternalId={contactInternalId}
      isSidebarOpen={isSidebarOpen}
      onToggleSidebar={onToggleSidebar}
      unreadMessagesCount={unreadMessagesCount}
      arePermissionsEnabled={arePermissionsEnabled}
      message={message}
      isConnectionAvailable={isConnectionAvailable}
    />;
  }
}

export default withGlobals(withNavigation(withAlert(VideoCallLobby)));



// WEBPACK FOOTER //
// ./containers/VideoCallLobby/VideoCallLobby.js